import { BracketNodeBase } from "./BracketGraphBase";
import { BracketMatch, isSeedSource } from "./BracketMatch";
import { onlyUnique, pushX } from "Utility";
import {
  depthToShorterTitleDoubleElim,
  depthToShorterTitleSingleElim,
} from "./BracketTitles";
import { BracketType } from "model/Tournament/Segment/Bracket/Bracket";
// todo - better name?
export type BracketGraph = {
  finalNodes: BracketNode[];
};

// depth excludes Grand Final, e.g. depth 1 is UF/LF, depth 0 is GF
export type BracketNode = BracketNodeBase & {
  depth: number;
  isLower: boolean;
  match: BracketMatch;
  teamOneSource: BracketNode | undefined;
  teamTwoSource: BracketNode | undefined;
  temp: string;
  pickerTeamOneId?: string;
  pickerTeamTwoId?: string;
  matchTitle?: string;
};

export const isBracketNode = (node: BracketNodeBase): node is BracketNode => {
  return (node as BracketNode).temp !== undefined;
};

export const getAllNodes = (nodes: BracketNode[]) => {
  return nodes
    .flatMap((n) => getAllChildrenNodesAndSelfRecursive(n))
    .filter(onlyUnique);
};

export const getMaxDepth = (nodes: BracketNode[]) => {
  return Math.max(...nodes.map((n) => n.depth));
};

export const getMaxLowerDepth = (graph: BracketGraph) => {
  const nodes = getAllNodes(graph.finalNodes).filter((n) => n.isLower);
  return getMaxDepth(nodes);
};

export const getMaxUpperDepth = (graph: BracketGraph) => {
  const nodes = getAllNodes(graph.finalNodes).filter((n) => !n.isLower);
  return getMaxDepth(nodes);
};

export const bracketGraphHasGrandFinal = (graph: BracketGraph) => {
  return graph.finalNodes.length === 1;
};

export const nodeGetsLosingTeam = (
  node: BracketNode,
  sourceNode: BracketNode | undefined
) => {
  return node.isLower && !!sourceNode && !sourceNode.isLower;
};

export const getNodeMatchTitle = (
  node: BracketNode,
  bracketType: BracketType,
  maxDepth: number
) => {
  if (bracketType === "SingleElim") {
    return depthToShorterTitleSingleElim(node.depth, maxDepth);
  }

  if (bracketType === "DoubleElim") {
    return depthToShorterTitleDoubleElim(node.depth, maxDepth, node.isLower);
  }

  return undefined;
};

export const getBracketGraphMatchTitle = (
  graph: BracketGraph,
  bracketType: BracketType,
  bracketName: string | undefined,
  matchId: string
) => {
  const allNodes = getAllNodes(graph.finalNodes);
  const node = allNodes.find((n) => n.match.matchId === matchId);
  if (!node) {
    return undefined;
  }

  const maxDepth = node.isLower
    ? getMaxLowerDepth(graph)
    : getMaxUpperDepth(graph);
  return `${!!bracketName ? `${bracketName}` : ""} ${getNodeMatchTitle(
    node,
    bracketType,
    maxDepth
  )}`;
};

// Note - not explicitly ordered
const getAllChildrenNodesAndSelfRecursive = (node: BracketNode) => {
  let nodes: BracketNode[] = [node];
  if (!!node.teamOneSource) {
    nodes = nodes.concat(
      getAllChildrenNodesAndSelfRecursive(node.teamOneSource)
    );
  }

  if (!!node.teamTwoSource) {
    nodes = nodes.concat(
      getAllChildrenNodesAndSelfRecursive(node.teamTwoSource)
    );
  }

  return nodes;
};

export const getsLosingTeam = (
  node: BracketNode,
  sourceNode: BracketNode | undefined
) => {
  return node.isLower && !!sourceNode && !sourceNode.isLower;
};

export const getBracketNode = (node: BracketNode, matchId: string) => {
  const allNodes = getAllChildrenNodesAndSelfRecursive(node);
  return allNodes.find((n) => n.match.matchId === matchId);
};

export const buildBracketGraphAndAssignMatchTitles = (
  matches: BracketMatch[],
  bracketType: BracketType
) => {
  const graph = buildBracketGraph(matches, bracketType);
  assignMatchTitles(graph, bracketType);
  return graph;
};

const buildBracketGraph = (
  matches: BracketMatch[],
  bracketType: BracketType
): BracketGraph | undefined => {
  const finalMatches = matches.filter((m) => m.isFinal);
  if (finalMatches.length === 0) {
    return undefined;
  }

  if (bracketType === "SingleElim") {
    return {
      finalNodes: [
        createBracketNodeRecursive(
          finalMatches[0], // todo - SingleElim to deal with multi finals
          matches,
          false,
          1
        ),
      ],
    };
  }

  if (finalMatches.length === 1) {
    const finalMatch = finalMatches[0];
    const upperFinalMatchId = finalMatch.teamOneSource;
    const lowerFinalMatchId = finalMatch.teamTwoSource;
    let upperFinalNode: BracketNode | undefined;
    let lowerFinalNode: BracketNode | undefined;

    if (!!upperFinalMatchId && !isSeedSource(upperFinalMatchId)) {
      const upperFinalMatch = matches.find(
        (m) => m.matchId === upperFinalMatchId
      );
      if (!upperFinalMatch) {
        return undefined;
      }

      upperFinalNode = createBracketNodeRecursive(
        upperFinalMatch,
        matches,
        false,
        1
      );
    }

    if (!lowerFinalMatchId || isSeedSource(lowerFinalMatchId)) {
      // set as undefined
    } else {
      const lowerFinalMatch = matches.find(
        (m) => m.matchId === lowerFinalMatchId
      );
      if (!lowerFinalMatch) {
        return undefined;
      }
      const upperFinalNodes = !!upperFinalNode
        ? getAllChildrenNodesAndSelfRecursive(upperFinalNode)
        : [];

      lowerFinalNode = createBracketNodeRecursive(
        lowerFinalMatch,
        matches,
        true,
        1,
        upperFinalNodes
      );
    }
    const finalNode: BracketNode = {
      isLower: false,
      match: finalMatch,
      teamOneSource: upperFinalNode,
      teamTwoSource: lowerFinalNode,
      depth: 0,
      temp: "temp",
    };

    return { finalNodes: [finalNode] };
  }

  const finalNodes: BracketNode[] = [];
  const upperNodes: BracketNode[] = [];
  const upperFinalMatches = finalMatches.filter((m) => m.isUpperFinal);
  upperFinalMatches.forEach((upperFinalMatch) => {
    const finalNode = createBracketNodeRecursive(
      upperFinalMatch,
      matches,
      false,
      1
    );
    finalNodes.push(finalNode);
    upperNodes.push(...getAllChildrenNodesAndSelfRecursive(finalNode));
  });

  const lowerFinalMatches = finalMatches.filter((m) => !m.isUpperFinal);
  lowerFinalMatches.forEach((lowerFinalMatch) => {
    const finalNode = createBracketNodeRecursive(
      lowerFinalMatch,
      matches,
      true,
      1,
      upperNodes
    );
    finalNodes.push(finalNode);
  });

  return { finalNodes };
};

const createBracketNodeRecursive = (
  match: BracketMatch,
  matches: BracketMatch[],
  isLower: boolean,
  depth: number,
  existingNodes: BracketNode[] = []
): BracketNode => {
  let teamOneSourceMatch: BracketMatch | undefined = undefined;
  if (!isSeedSource(match.teamOneSource)) {
    teamOneSourceMatch = matches.find((m) => m.matchId === match.teamOneSource);
  }

  let teamTwoSourceMatch: BracketMatch | undefined = undefined;
  if (!isSeedSource(match.teamTwoSource)) {
    teamTwoSourceMatch = matches.find((m) => m.matchId === match.teamTwoSource);
  }

  const existingTeamOneSourceNode = !!teamOneSourceMatch
    ? existingNodes.find((n) => n.match === teamOneSourceMatch)
    : undefined;
  const existingTeamTwoSourceNode = !!teamTwoSourceMatch
    ? existingNodes.find((n) => n.match === teamTwoSourceMatch)
    : undefined;

  return {
    isLower,
    match,
    teamOneSource: !!existingTeamOneSourceNode
      ? existingTeamOneSourceNode
      : !!teamOneSourceMatch
      ? createBracketNodeRecursive(
          teamOneSourceMatch,
          matches,
          isLower,
          depth + 1,
          existingNodes
        )
      : undefined,
    teamTwoSource: !!existingTeamTwoSourceNode
      ? existingTeamTwoSourceNode
      : !!teamTwoSourceMatch
      ? createBracketNodeRecursive(
          teamTwoSourceMatch,
          matches,
          isLower,
          depth + 1,
          existingNodes
        )
      : undefined,
    depth,
    temp: "temp",
  };
};

export const getNodesByDepth = (
  firstNodes: BracketNode[],
  isLower: boolean
) => {
  const maxDepth =
    getMaxDepth(getAllNodes(firstNodes)) - firstNodes[0]?.depth ?? 0;
  const nodesByDepth: (BracketNode | undefined)[][] = [];
  firstNodes.forEach((n) => {
    addNodeRecursive(n, nodesByDepth, isLower, 0, maxDepth);
  });

  return nodesByDepth;
};

// todo - needs to deal with Single Elim / GFs better
export const addNodeRecursive = (
  node: BracketNode,
  nodesByDepth: (BracketNode | undefined)[][],
  isLower: boolean,
  depth: number,
  maxDepth: number
) => {
  if (node.isLower !== isLower) {
    return;
  }

  if (!nodesByDepth[depth]) {
    nodesByDepth[depth] = [];
  }

  nodesByDepth[depth].push(node);
  if (!!node.teamOneSource) {
    addNodeRecursive(
      node.teamOneSource,
      nodesByDepth,
      isLower,
      depth + 1,
      maxDepth
    );
  } else if (depth < maxDepth) {
    var multiplier = 1;
    for (var i = depth + 1; i <= maxDepth; i++) {
      if (!nodesByDepth[i]) {
        nodesByDepth[i] = [];
      }

      pushX(undefined, multiplier, nodesByDepth[i]);
    }

    multiplier *= 2;
  }

  if (!!node.teamTwoSource) {
    addNodeRecursive(
      node.teamTwoSource,
      nodesByDepth,
      isLower,
      depth + 1,
      maxDepth
    );
  } else if (depth < maxDepth) {
    var multiplier = 1;
    for (var i = depth + 1; i <= maxDepth; i++) {
      if (!nodesByDepth[i]) {
        nodesByDepth[i] = [];
      }

      pushX(undefined, multiplier, nodesByDepth[i]);
    }

    multiplier *= 2;
  }
};

// todo - cleanup
export const getChildrenNodesByDepth = (firstNodes: BracketNode[]) => {
  var upperNodesByDepth: (BracketNode | undefined)[][] = [];
  var lowerNodesByDepth: (BracketNode | undefined)[][] = [];

  if (firstNodes.length === 1) {
    const firstNode = firstNodes[0];
    if (!!firstNode.teamOneSource) {
      upperNodesByDepth = getNodesByDepth([firstNode.teamOneSource], false);
    }

    if (!!firstNode.teamTwoSource) {
      lowerNodesByDepth = getNodesByDepth([firstNode.teamTwoSource], true);
    }
  } else {
    firstNodes.forEach((n) => {
      if (n.isLower) {
        const nodesByDepth = getNodesByDepth([n], true);
        nodesByDepth.forEach((nodes, i) => {
          if (lowerNodesByDepth.length >= i + 1) {
            lowerNodesByDepth[i].push(...nodes);
          } else {
            lowerNodesByDepth.push(nodes);
          }
        });
      } else {
        const nodesByDepth = getNodesByDepth([n], false);
        nodesByDepth.forEach((nodes, i) => {
          if (upperNodesByDepth.length >= i + 1) {
            upperNodesByDepth[i].push(...nodes);
          } else {
            upperNodesByDepth.push(nodes);
          }
        });
      }
    });
  }

  return {
    upperNodesByDepth,
    lowerNodesByDepth,
  };
};

const assignMatchTitles = (
  graph: BracketGraph | undefined,
  bracketType: BracketType
) => {
  if (!graph) {
    return;
  }

  if (bracketType === "SingleElim") {
    const allNodes = getAllNodes(graph.finalNodes);
    const maxDepth = getMaxDepth(allNodes);
    allNodes.forEach((n) => {
      n.matchTitle = depthToShorterTitleSingleElim(n.depth, maxDepth);
    });
  } else if (bracketType === "DoubleElim") {
    const allNodes = getAllNodes(graph.finalNodes);
    const maxUpperDepth = getMaxUpperDepth(graph);
    const maxLowerDepth = getMaxLowerDepth(graph);
    allNodes.forEach((n) => {
      n.matchTitle = depthToShorterTitleDoubleElim(
        n.depth,
        n.isLower ? maxLowerDepth : maxUpperDepth,
        n.isLower
      );
    });
  }
};
